// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { Context, ProductListing, Search } from "@coveo/headless/commerce";
import { getCookieValue } from "@DSG/Coveo/Contex/Engine";
import { CoveoHandler, CoveoStrictInputDispatch } from "@DSG/Store/CoveoHandlerCreator/CoveoHandlerCreator";
import { CoveoCommerceState } from "@DSG/Store/Data/CoveoCommerceState";
import { DISPATCH_BREADCRUMB_MANAGER_CONTROLLER, DISPATCH_COVEO_IS_CONFIGURING, DISPATCH_COVEO_IS_INITIALIZED, DISPATCH_MAIN_COVEO_PRODUCT_CONTROLLER, DISPATCH_RESET_SEARCH_INPUT_QUERY, DISPATCH_SORT_CONTROLLER, DISPATCH_UPDATE_BREADCRUMB_MANAGER_STATE, DISPATCH_UPDATE_FACET_MANAGER_STATE, DISPATCH_UPDATE_PAGINATION_STATE, DISPATCH_UPDATE_PRODUCT_LISTING_STATE, DISPATCH_UPDATE_SEARCH_STATE, DISPATCH_UPDATE_SORT_STATE, DISPATCH_UPDATE_UN_SUB_COVEO_UI_CONFIG } from "@DSG/Store/Reducer/CoveoCommerceReducer";
import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";

export interface Parameter {
    page: string;
    coveoCommerce: CoveoCommerceState;
    defaultPageSize:number;
}

export type HandleType = Handler<Parameter, CoveoHandler>;

const bindUrlManager = (controller:  Search | ProductListing, dispatch: CoveoStrictInputDispatch) => {
    if (!controller) {
        // eslint-disable-next-line no-console
        console.warn('No controller found >>');
        return;
    }

    const fragment = () => window.location.hash.slice(1);

    const urlManager = controller.urlManager({
        initialState: { fragment: fragment() },
    });

    const onHashChange = () => {
        // console.log('hash changed >>', window.location.pathname, window.location.hash);
        // console.log('fragment >>', fragment());
        urlManager.synchronize(fragment());
    };

    window.addEventListener('hashchange', onHashChange);
    // console.log("AFTER EVENT LISTENER >>", window.location.hash);

    const unsubscribeManager = urlManager.subscribe(() => {       
        const path = window.location.pathname;
        // Capture all existing query parameters from the current URL
        const currentUrl = new URL(window.location.href);

        if(path !=="/noSearchResult"){
            // Construct the URL with existing query parameters and fragment 
            const url = `${path}${currentUrl.search}#${urlManager.state.fragment}`;

            if (!controller.state.responseId) {
                // console.log('Replace url >>', url);
                window.history.replaceState({}, document.title, url);
                return;
            }
            // console.log('Push url >>', url);
            window.history.pushState({}, document.title, url);
        }
    });

    return () => {
        // console.log('unsubscribing url manager >>');
        window.removeEventListener('hashchange', onHashChange);
        unsubscribeManager();
    }; 
}

const setSubControllers = (
    controller: Search | ProductListing,
    context: Context | null,
    dispatch: CoveoStrictInputDispatch
) => {
    const pagination = controller.pagination();
    pagination.subscribe(() => dispatch({
        type: DISPATCH_UPDATE_PAGINATION_STATE,
        state: pagination.state
    }));

    // Sort
    const sort = controller.sort();
    dispatch({
        type:DISPATCH_SORT_CONTROLLER,
        sort
    });
    sort.subscribe(() => dispatch({
        type: DISPATCH_UPDATE_SORT_STATE,
        state: sort.state
    }));

    // Breadcrumb
    const breadcrumbManager = controller.breadcrumbManager();
    dispatch({
        type: DISPATCH_BREADCRUMB_MANAGER_CONTROLLER,
        breadcrumbManager
    });
    breadcrumbManager.subscribe(() => dispatch({
        type: DISPATCH_UPDATE_BREADCRUMB_MANAGER_STATE,
        state: breadcrumbManager.state
    }));

    const facetController = controller.facetGenerator();
    facetController.subscribe(() => dispatch({
        type: DISPATCH_UPDATE_FACET_MANAGER_STATE,
        state: facetController.facets
    }));

    const commerceView = getCookieValue('commerceView');
    const path = window.location.pathname;
    const url = `${commerceView}${path}`;
    context?.setView({ url });

    dispatch({
        type: DISPATCH_UPDATE_UN_SUB_COVEO_UI_CONFIG,
        unSub: bindUrlManager(controller, dispatch) || (() => {})
    });
};

const combinedConfigurations = (
    page: string,
    coveoCommerce: CoveoCommerceState, 
    defaultPageSize: number,
    dispatch: CoveoStrictInputDispatch
) => {
    if(!page || !coveoCommerce) { return; }
    const isSearchPage = page.includes('/search');
    const {
        Context,
        ProductListingController,
        ProductListingState,
         SearchState,
         SearchController,
         SearchBoxController,
         UnSubCoveoUIConfigs
    } = coveoCommerce;

    const mainController = isSearchPage ? SearchController : ProductListingController;
    if (!mainController || !Context) { 
        dispatch({
            type: DISPATCH_COVEO_IS_INITIALIZED,
            isInitialized: false
        });
        return; 
    }

    dispatch({
        type: DISPATCH_COVEO_IS_INITIALIZED,
        isInitialized: true
    });

    dispatch({
        type: DISPATCH_COVEO_IS_CONFIGURING,
        isConfiguring: true
    });
    
    // Only call UnSubCoveoUIConfigs if it exists and is a function
    if (typeof UnSubCoveoUIConfigs === 'function') {
        UnSubCoveoUIConfigs();
    }

    dispatch({
        type: DISPATCH_MAIN_COVEO_PRODUCT_CONTROLLER,
        controller: mainController
    });

    dispatch({
        type: isSearchPage ? DISPATCH_UPDATE_SEARCH_STATE : DISPATCH_UPDATE_PRODUCT_LISTING_STATE,
        state: {
            ...(isSearchPage ? ProductListingState : SearchState),
            products: [],
            error: null,
            isLoading: false,
            responseId: ""
        }
    });

    setSubControllers(mainController, Context, dispatch);
    if(isSearchPage) {
        if (!SearchController?.state.isLoading && !SearchController?.state.responseId) {
            SearchController?.executeFirstSearch();
        } else if (!SearchController?.state.isLoading) {
            SearchBoxController?.submit();
        }
    } else {
        ProductListingController?.refresh();
    }   

    dispatch({
        type: DISPATCH_RESET_SEARCH_INPUT_QUERY,
    });

    dispatch({
        type: DISPATCH_COVEO_IS_CONFIGURING,
        isConfiguring: false
    });
}

export const CoveoUIPageConfiguration: HandleType = (props) => {
    const { dispatch, parameter } = props;
    const { page, coveoCommerce,defaultPageSize } = parameter;

    if (!page || !coveoCommerce) { return; }
    combinedConfigurations(page, coveoCommerce,defaultPageSize, dispatch as CoveoStrictInputDispatch);
};

export const chain = [CoveoUIPageConfiguration];

const CoveoUIConfigHandler = createHandlerChainRunner(chain, "CoveoUIConfigHandler");

export default CoveoUIConfigHandler;